import React, { useState, useMemo, useCallback } from "react";
import CalendarHeader from "../components/aesthetics/calendar/CalendarHeader";
import CalendarGrid from "../components/aesthetics/calendar/CalendarGrid";
import { Booking } from "../types/booking";
import { startOfDayISO } from "../lib/dateUtil";

// Aesthetics-specific practitioners
const aestheticsPractitioners = [
  { id: 1, name: "Dr. Saleema", color: "#885ABB" },
  { id: 2, name: "Emma Taylor", color: "#5AB7BB" },
  { id: 3, name: "Dr. Harper", color: "#C96EBE" },
  { id: 4, name: "Kate Morgan", color: "#837EED" },
  { id: 5, name: "Emma Griffin", color: "#FFB74D" },
  { id: 6, name: "Sophie Chen", color: "#4DB6AC" },
  { id: 7, name: "Dr. Williams", color: "#E57373" },
  { id: 8, name: "Lucy Adams", color: "#64B5F6" },
];

// Aesthetics-specific treatments
const aestheticsTreatments = [
  "Botox - Upper Face",
  "Botox - Crow's Feet",
  "Dermal Fillers - Lips",
  "Dermal Fillers - Cheeks",
  "Hydrafacial Treatment",
  "Chemical Peel - TCA",
  "Chemical Peel - Glycolic",
  "Microneedling",
  "PRP Facial Rejuvenation",
  "LED Light Therapy",
  "Laser Hair Removal",
  "Skin Tightening - RF",
  "Acne Treatment",
  "Consultation",
  "Follow-up Appointment",
];

// Aesthetics client names
const aestheticsClients = [
  "Sarah Miller",
  "Emma Clark",
  "Rachel Adams",
  "Laura Brown",
  "Sophie Anderson",
  "Michael Davis",
  "David Wilson",
  "Olivia Martinez",
  "Isabella Johnson",
  "William Thompson",
  "Ethan Brown",
  "Charlotte White",
  "James Mitchell",
  "Ava Taylor",
  "Mia Roberts",
  "Oliver Harris",
  "Amelia Clark",
  "Sophia Lee",
  "Emily Watson",
  "Daniel Moore",
];

const AestheticCalendar: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(startOfDayISO(new Date()));
  const [endDate, setEndDate] = useState<string>(startOfDayISO(new Date()));
  const [status, setStatus] = useState<string>("all");
  const [selectedStaff, setSelectedStaff] = useState<string | null>("");
  const [calendarView, setCalendarView] = useState<"day" | "week">("day");
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());
  const [refreshKey, setRefreshKey] = useState(0);

  const refreshBookings = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Generate static aesthetics bookings
  const staticBookings = useMemo(() => {
    const generated: Booking[] = [];
    const durations = [30, 45, 60, 90, 120]; // Treatment durations in minutes

    // Seed random number generator based on date for consistency
    const dateSeed = calendarDate.toDateString();
    let seedValue = 0;
    for (let i = 0; i < dateSeed.length; i++) {
      seedValue += dateSeed.charCodeAt(i);
    }
    const seededRandom = (max: number) => {
      seedValue = (seedValue * 9301 + 49297) % 233280;
      return Math.floor((seedValue / 233280) * max);
    };

    aestheticsPractitioners.forEach((practitioner, pIndex) => {
      const baseDate = new Date(calendarDate);
      baseDate.setHours(9, 0, 0, 0);
      let currentTime = new Date(baseDate);

      const endTimeLimit = new Date(baseDate);
      endTimeLimit.setHours(18, 0, 0, 0); // Aesthetics clinic closes at 6 PM

      let idx = 0;
      while (currentTime < endTimeLimit && idx < 8) {
        // Max 8 appointments per practitioner
        const shouldSkip = seededRandom(100) < 30; // 30% chance of gap
        const duration = durations[seededRandom(durations.length)];
        const start = new Date(currentTime);
        const end = new Date(start.getTime() + duration * 60000);

        if (end > endTimeLimit) break;

        if (!shouldSkip) {
          const statusOptions: ("booked" | "completed" | "pending" | "in progress")[] = [
            "booked",
            "booked",
            "booked",
            "completed",
            "pending",
            "in progress",
          ];
          const randomStatus = statusOptions[seededRandom(statusOptions.length)];
          const treatment = aestheticsTreatments[seededRandom(aestheticsTreatments.length)];
          const client = aestheticsClients[seededRandom(aestheticsClients.length)];
          const price = [85, 120, 150, 180, 220, 280, 350, 450][seededRandom(8)];

          // Treatment-specific colors
          const treatmentColors: { [key: string]: string } = {
            "Botox": "#885ABB",
            "Dermal Fillers": "#C96EBE",
            "Hydrafacial": "#5AB7BB",
            "Chemical Peel": "#837EED",
            "Microneedling": "#FFB74D",
            "PRP": "#4DB6AC",
            "LED Light": "#64B5F6",
            "Laser": "#E57373",
            "Skin Tightening": "#F06292",
            "Acne": "#81C784",
            "Consultation": "#90A4AE",
            "Follow-up": "#A1887F",
          };

          // Find matching color
          let categoryColor = practitioner.color;
          for (const [key, color] of Object.entries(treatmentColors)) {
            if (treatment.includes(key)) {
              categoryColor = color;
              break;
            }
          }

          generated.push({
            id: 100000 + pIndex * 1000 + idx + refreshKey * 10000,
            client: `${client} - ${treatment}`,
            start: start.toISOString(),
            end: end.toISOString(),
            staff: practitioner.name,
            status: randomStatus,
            amountPaid: randomStatus === "completed" ? price : 0,
            amountToPay: price,
            currency: "£",
            categoryColor: categoryColor,
          });

          // Add a small gap between appointments
          const gap = (1 + seededRandom(2)) * 15;
          currentTime = new Date(end.getTime() + gap * 60000);
        } else {
          // Skip to next slot
          const jump = (2 + seededRandom(4)) * 15;
          currentTime = new Date(currentTime.getTime() + jump * 60000);
        }
        idx++;
      }
    });

    return generated;
  }, [calendarDate, refreshKey]);

  // Filter bookings based on status
  const filteredBookings = useMemo(() => {
    if (status === "all") return staticBookings;
    return staticBookings.filter((b) => b.status === status);
  }, [staticBookings, status]);

  return (
    <div>
      <div className="min-h-screen bg-gray-100">
        <CalendarHeader
          startDate={startDate}
          endDate={endDate}
          status={status}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onStatusChange={setStatus}
          staffList={aestheticsPractitioners}
          onStaffChange={setSelectedStaff}
          view={calendarView}
          setView={setCalendarView}
          selectedDate={calendarDate}
          setSelectedDate={setCalendarDate}
        />

        <div className="relative">
          <CalendarGrid
            bookings={filteredBookings}
            selectedStaff={selectedStaff}
            view={calendarView}
            selecteddate={calendarDate}
            staffList={aestheticsPractitioners}
            onRefresh={refreshBookings}
          />
        </div>
      </div>
    </div>
  );
};

export default AestheticCalendar;

